import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import { InputAdornment, Popover, TextField, Tooltip } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { useDateFormatter } from '../../generic/hooks/useDateFormatter'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import PeriodicReturnField from './PeriodicReturnField'
import { periodicReturnSummary } from './periodicReturnSummary'

/**
 * Story 182 (issue #3015) — the PERIODIC rule inside the node's general properties row.
 *
 * That row never wraps (it scrolls horizontally, see the comment on the Return timing block), so the rule
 * editor cannot sit in it: it would stretch the whole row. Instead the row gets a field of the same height as
 * its siblings that shows the readable sentence of the rule, and the editor opens in a popover anchored to it.
 * The field is compact (the row is already tight once the Index selector is shown): the sentence is cut with an
 * ellipsis and the whole of it is in the tooltip.
 *
 * - Incomplete rule while editing ⇒ the field is red, same signal as the inline errors of the editor.
 * - Just switched to Periodic (no rule yet) ⇒ the popover opens by itself, so choosing the mode leads
 *   straight to filling the rule.
 * - Read only ⇒ the field still opens the popover, with every control disabled, to read the details.
 *
 * @param {Object} props
 * @param {Object|null} props.rule - the `periodicRule` of the job's `externalReturn`
 * @param {boolean} props.disabled
 * @param {function(Object)} props.onChange - receives the whole new rule
 */
const PeriodicReturnPopoverField = ({ rule, disabled, onChange }) => {
  const { t_, i18n } = useSmartTranslation()
  const { formatDate } = useDateFormatter()
  const anchorRef = useRef(null)
  // The editor unmounts every time the popover closes; the per-form drafts live here so that closing and
  // reopening (or the time picker's own popup stealing the click) does not forget the other form's values.
  const draftsRef = useRef({})
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (rule === null && !disabled) {
      setOpen(true)
    }
    // Only on mount: the mode was just switched to Periodic.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const summary = periodicReturnSummary(rule, { t_, formatDate, language: i18n?.language })
  const ActionIcon = disabled ? VisibilityOutlinedIcon : EditOutlinedIcon

  return (
    <>
      <Tooltip title={open ? '' : (summary ?? '')} placement='top' arrow disableInteractive>
        <TextField
          ref={anchorRef}
          label={t_('Return rule')}
          value={summary ?? ''}
          size='small'
          error={!disabled && !summary}
          onClick={() => setOpen(true)}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            readOnly: true,
            startAdornment: <InputAdornment position='start'><EventRepeatOutlinedIcon fontSize='small' color={summary ? 'primary' : 'inherit'} /></InputAdornment>,
            endAdornment: <InputAdornment position='end'><ActionIcon fontSize='small' /></InputAdornment>
          }}
          inputProps={{ 'aria-haspopup': 'dialog', 'data-testid': 'periodic-return-trigger' }}
          sx={{
            width: '14rem',
            flexShrink: 0,
            cursor: 'pointer',
            '& .MuiInputBase-root, & .MuiInputBase-input': { cursor: 'pointer' },
            '& .MuiInputBase-input': { textOverflow: 'ellipsis' }
          }}
        />
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.75, width: 460, maxWidth: 'calc(100vw - 32px)', borderRadius: 2 } } }}
      >
        <PeriodicReturnField rule={rule} disabled={disabled} onChange={onChange} embedded draftsRef={draftsRef} />
      </Popover>
    </>
  )
}

export default PeriodicReturnPopoverField
